import { useEffect, useRef, useState } from "react";
import "./PosterPlaceDialog.css";

export interface PosterPlaceDialogProps {
    position: { x: number; y: number };
    onPlace: (quantity: number) => void;
    onClose: () => void;
    isLoading?: boolean;
}

const QUICK_QUANTITIES = [1, 2, 4, 6];

export const PosterPlaceDialog = ({
    position,
    onPlace,
    onClose,
    isLoading = false
}: PosterPlaceDialogProps): JSX.Element => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const [customQty, setCustomQty] = useState("");
    const [showCustom, setShowCustom] = useState(false);

    useEffect(() => {
        let mouseDownOutside = false;
        const handleMouseDown = (e: MouseEvent) => {
            mouseDownOutside = !!dialogRef.current && !dialogRef.current.contains(e.target as Node);
        };
        const handleMouseUp = (e: MouseEvent) => {
            if (mouseDownOutside && dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
                onClose();
            }
            mouseDownOutside = false;
        };
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [onClose]);

    const handleCustomSubmit = () => {
        const n = parseInt(customQty, 10);
        if (n >= 1 && n <= 1000) {
            onPlace(n);
        }
    };

    return (
        <div
            ref={dialogRef}
            className="poster-place-dialog"
            style={{ left: position.x, top: position.y }}
        >
            <div className="poster-place-dialog__title">Placera affisch</div>

            {!showCustom ? (
                <>
                    <div className="poster-place-dialog__quantities">
                        {QUICK_QUANTITIES.map((q) => (
                            <button
                                key={q}
                                type="button"
                                className="poster-place-dialog__qty-btn"
                                onClick={() => onPlace(q)}
                                disabled={isLoading}
                            >
                                {q}
                            </button>
                        ))}
                        <button
                            type="button"
                            className="poster-place-dialog__qty-btn poster-place-dialog__qty-btn--custom"
                            onClick={() => setShowCustom(true)}
                            disabled={isLoading}
                        >
                            ✎
                        </button>
                    </div>
                    <button
                        type="button"
                        className="poster-place-dialog__cancel"
                        onClick={onClose}
                    >
                        Avbryt
                    </button>
                </>
            ) : (
                <div className="poster-place-dialog__custom">
                    <input
                        type="number"
                        className="poster-place-dialog__custom-input"
                        min={1}
                        max={1000}
                        placeholder="Antal"
                        value={customQty}
                        onChange={(e) => setCustomQty(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleCustomSubmit();
                            if (e.key === "Escape") setShowCustom(false);
                        }}
                    />
                    <div className="poster-place-dialog__custom-actions">
                        <button
                            type="button"
                            className="poster-place-dialog__qty-btn"
                            onClick={handleCustomSubmit}
                            disabled={isLoading || !customQty}
                        >
                            OK
                        </button>
                        <button
                            type="button"
                            className="poster-place-dialog__cancel"
                            onClick={() => setShowCustom(false)}
                        >
                            Tillbaka
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
